// apiService.js
import { jwtDecode } from "jwt-decode";

class CustomError extends Error {
  constructor(message, code) {
    super(message);
    this.code = code;
  }
}

// Shared state to prevent multiple simultaneous refresh calls
let isRefreshing = false;
let refreshPromise = null;

class ApiService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_BASE_URL;
  }

  // Helper method to get translations (you'll need to pass these in)
  getErrorMessages() {
    return {
      error_session_expired: "Session expired",
      error_server: "Server error",
      error_something_wrong: "Something went wrong",
    };
  }

  isTokenExpired(token) {
    try {
      const { exp } = jwtDecode(token);
      return Date.now() >= exp * 1000;
    } catch (err) {
      return true;
    }
  }

  async refreshAccessToken() {
    // If already refreshing, return the existing promise
    if (isRefreshing && refreshPromise) {
      return await refreshPromise;
    }

    // Start refresh process
    isRefreshing = true;
    refreshPromise = this.performTokenRefresh()
      .then((newToken) => {
        isRefreshing = false;
        refreshPromise = null;
        return newToken;
      })
      .catch((error) => {
        isRefreshing = false;
        refreshPromise = null;
        throw error;
      });

    return await refreshPromise;
  }

  async performTokenRefresh() {
    try {
      const ps10004000 = JSON.parse(localStorage.getItem("ps10004000"));
      const ps10005000 = JSON.parse(localStorage.getItem("ps10005000"));
      const encodedToken = jwtDecode(ps10005000);

      const response = await fetch(`${this.baseUrl}/User/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refreshToken: ps10004000,
          userId: encodedToken.uid,
        }),
      });

      if (!response.ok) {
        const errorMessages = this.getErrorMessages();
        throw new CustomError(errorMessages.error_session_expired, 999);
      }

      const data = await response.json();
      localStorage.setItem("ps10005000", JSON.stringify(data.token));
      localStorage.setItem("ps10004000", JSON.stringify(data.refreshToken));

      return data.token;
    } catch (err) {
      // Clear tokens on refresh failure
      localStorage.removeItem("ps10005000");
      localStorage.removeItem("ps10004000");
      throw err;
    }
  }

  async getValidToken() {
    let ps10005000 = JSON.parse(localStorage.getItem("ps10005000"));
    let token = ps10005000 ? ps10005000 : null;

    // If token is expired, refresh it
    if (token && this.isTokenExpired(token)) {
      token = await this.refreshAccessToken();
    }

    return token;
  }

  async apiCall(endpoint, method, options = {}) {
    try {
      // Get valid token (will refresh if needed)
      const token = await this.getValidToken();
      const siteLanguage = localStorage.getItem("language") || "en";

      const defaultHeaders = {
        "Content-Type": "application/json",
        "Accept-Language": siteLanguage,
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      });

      // Handle 401 - token might have expired between refresh and API call
      if (response.status === 401) {
        // Force refresh and retry once
        isRefreshing = false;
        refreshPromise = null;

        const newToken = await this.refreshAccessToken();

        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          method,
          ...options,
          headers: {
            ...defaultHeaders,
            Authorization: `Bearer ${newToken}`,
            ...options.headers,
          },
        });

        if (!retryResponse.ok) {
          throw new Error(
            `API call failed after token refresh: ${retryResponse.status}`,
          );
        }

        // Process retry response
        let retryData = {};
        try {
          const text = await retryResponse.text();
          try {
            retryData = JSON.parse(text);
          } catch (error) {
            retryData = text;
          }
        } catch (error) {
          console.error(error);
        }

        return retryData;
      }

      if (!response.ok) {
        let errorData = {};
        try {
          const text = await response.text();
          errorData = text ? JSON.parse(text) : {};
        } catch (error) {
          console.error(error);
        }

        const errorMessages = this.getErrorMessages();
        const errorMessage =
          Number(errorData.status) === 500
            ? errorMessages.error_server
            : errorData.detail ||
              (errorData.errors
                ? Object.values(errorData.errors)[0][0]
                : errorMessages.error_something_wrong);

        throw new Error(errorMessage);
      }

      let data = {};
      try {
        const text = await response.text();
        try {
          data = JSON.parse(text);
        } catch (error) {
          data = text;
        }
      } catch (error) {
        console.error(error);
      }

      return data;
    } catch (err) {
      // You might want to handle this differently without React hooks
      console.error("API Error:", err.message);
      throw err;
    }
  }

  async get(endpoint, options = {}) {
    return this.apiCall(endpoint, "GET", options);
  }

  async post(endpoint, body, options = {}) {
    return this.apiCall(endpoint, "POST", {
      ...options,
      body: JSON.stringify(body),
    });
  }

  async put(endpoint, body, options = {}) {
    return this.apiCall(endpoint, "PUT", {
      ...options,
      body: JSON.stringify(body),
    });
  }

  async patch(endpoint, body, options = {}) {
    return this.apiCall(endpoint, "PATCH", {
      ...options,
      body: JSON.stringify(body),
    });
  }

  async del(endpoint, options = {}) {
    return this.apiCall(endpoint, "DELETE", options);
  }
}

// Export a singleton instance
export const apiService = new ApiService();
